<script setup lang="ts">
const { openLogin } = useAuth();
const { userMigrationNewDomain } = useFeatureFlagsNew();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { redirectToPendingSettings } = useUserValidations();

const migrationNewDomain = computed(() => {
  return userMigrationNewDomain.value.startsWith('https://')
    ? userMigrationNewDomain.value
    : `https://${userMigrationNewDomain.value}`;
});

function onFinishButtonClick() {
  if (isAuthenticated.value) {
    redirectToPendingSettings();
  } else {
    openLogin();
  }
}

watch(
  isAuthenticated,
  (newValue) => {
    if (newValue) {
      redirectToPendingSettings();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div :class="$style.migrationHomeMessage">
    <div :class="$style.migrationWrapper">
      <img
        alt="Home Banner"
        :class="$style.banner"
        :src="getAssetsUrlForCDN('/assets/images/migration.png')"
      />
      <article :class="$style.article">
        <p :class="$style.title">
          {{ $t('migration.home.title') }}
        </p>

        <template v-if="!isAuthenticated">
          <p>
            {{ $t('migration.home.description') }}
          </p>
          <ul :class="$style.listMigration">
            <li>
              {{ $t('migration.home.click_button') }}
              <button
                :class="[$style.enterBtn]"
                @click="openLogin"
              >
                “{{ $t('migration.home.enter') }}”
              </button>

              {{ $t('migration.home.button_location') }}
            </li>
            <li>
              {{ $t('migration.home.enter_data', { appName: appName() }) }}
            </li>
            <li>
              {{
                $t('migration.home.redirect_to_new_domain', {
                  newDomain: userMigrationNewDomain,
                })
              }}
            </li>
          </ul>
          <h2 :class="$style.title">
            {{ $t('migration.home.not_validated') }}
          </h2>
          <p>
            {{ $t('migration.home.finish_verification') }}
          </p>
        </template>

        <template v-else>
          <p>
            {{ $t('migration.home.description') }}
          </p>
          <ul :class="$style.listMigration">
            <li>
              {{ $t('migration.home.click_button') }}
              <button
                :class="[$style.enterBtn]"
                @click="openLogin"
              >
                “{{ $t('migration.home.validate') }}”
              </button>

              {{ $t('migration.home.button_location') }}
            </li>
            <li>
              {{ $t('migration.home.complete_data', { appName: appName() }) }}
            </li>
            <li>
              {{
                $t('migration.home.on_complete_verification', {
                  newDomain: userMigrationNewDomain,
                })
              }}
            </li>
          </ul>
        </template>

        <button
          :class="[$style.btn, 'btn-primary']"
          @click="onFinishButtonClick"
        >
          <span v-if="!isAuthenticated">
            {{ $t('migration.home.finish_transfer') }}
          </span>
          <span v-else>
            {{ $t('migration.home.verify_account') }}
          </span>
        </button>

        <NuxtLink
          :class="[$style.btn, $style.btnNewDomain]"
          external
          :to="migrationNewDomain"
        >
          {{ `${$t('migration.home.go_to')} ${userMigrationNewDomain}` }}
          <NuxtIcon name="external" />
        </NuxtLink>
      </article>
    </div>
  </div>
</template>

<style module lang="postcss">
.migrationHomeMessage {
  @apply pb-5 md:pb-8 flex flex-col items-center justify-center text-texts;

  min-height: calc(100vh - 120px);

  .migrationWrapper {
    @apply w-full max-w-md mx-auto bg-bg-secondary/60 rounded-lg;
  }

  .banner {
    @apply rounded-t-lg select-none;
  }

  .article {
    @apply p-6 md:p-8 grid gap-4 text-xs leading-4;
  }

  .title {
    @apply text-sm font-bold leading-tight;
  }

  .enterBtn {
    @apply text-primary underline;
  }

  .listMigration {
    @apply ml-4 list-decimal list-outside flex flex-col gap-1;
  }

  .btn {
    @apply text-xs py-3 px-5 font-bold mx-auto rounded-lg;

    &.btnNewDomain {
      @apply py-0 text-texts/60 font-semibold flex items-center gap-1;
    }
  }
}
</style>
